import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useEffect, useMemo, useState } from 'react';
import cn from 'classnames';
import { AnimatePresence, motion } from 'framer-motion';
import { Button, Checkbox, Column, Row, TextButton, Tooltip, Typography } from 'seagull';
import { getTestIds } from 'seagull/utils/TestIds';
import { useCobConfig } from '~/client-flow/clientOb/CobConfigContext';
import { MobileSign } from '~/client-flow/clientOb/components/MobileSign';
import { useResponsive } from '~/client-flow/contexts';
import BankSecurityRow from '~/components/BankSecurityRow/BankSecurityRow';
import { useStore } from '~/contexts';
import { useFeatureFlags } from '~/hooks/useFeatureFlags';
import { useLocaleKeys } from '~/i18n';
import { Intent } from '~/infra/access-control';
import { relationshipModel } from '~/models';
import { selectors } from '~/store/state-selectors';
import { approveDisabledReasonsTextsPath, COBStep, getIsAllowedToSIgn, reasonToText } from '../../../ClientOnboarding.utils';
import { getNextStepButtonText, nextStepButtonTextsPath } from '../../ClientOnboardingInner.utils';
import { useSignAndApproveContext } from '../../hooks/useSignAndApprove';
import { GetPaymentMethodTID } from '../Steps/PaymentMethod/PaymentMethod';
import desktopStyles from '../Steps/StepsContainer/StepsContainerDesktop.module.scss';
import styles from './Footer.module.scss';
const ANCHOR_TOS_URL = 'https://www.sayanchor.com/terms-of-contact';
const collapseVariants = {
    hidden: {
        height: 0,
        opacity: 0,
        marginBottom: 0
    },
    visible: {
        height: 'auto',
        opacity: 1,
        marginBottom: 12
    }
};
const collapseTransition = {
    duration: 0.3,
    ease: 'easeInOut'
};
export const GetFooterTID = (parentTestId)=>getTestIds({
        parentTestId
    });
export const Footer = (param)=>{
    let { currentStep, relationship, onNext, testId, isNextDisabled, hideNextButton, statement, isStatementAccepted, onStatementChange } = param;
    const { t: footerTexts } = useLocaleKeys('clientFlow.footer');
    const { t: clientFlowTexts } = useLocaleKeys(nextStepButtonTextsPath);
    const { t: reasonTexts } = useLocaleKeys(approveDisabledReasonsTextsPath);
    const { isMobile } = useResponsive();
    const { CUSTOM_PAGES } = useFeatureFlags();
    const { FooterTestIds } = useMemo(()=>GetFooterTID(testId), [
        testId
    ]);
    const { PaymentMethodTestIds } = useMemo(()=>GetPaymentMethodTID(), []);
    const nextStepButtonText = getNextStepButtonText(currentStep, relationship, clientFlowTexts, {
        customPagesFFEnabled: !!CUSTOM_PAGES
    });
    const showCheckbox = statement && onStatementChange;
    const isSignAndApproveStep = currentStep === COBStep.signAndApprove;
    const { signature, disabledReason, unacceptedStatements, navigateToTermsStep, hasNoServiceCommitted } = useSignAndApproveContext();
    const { isPreview } = useCobConfig();
    const userId = useStore(selectors.user.id);
    const role = useStore(selectors.user.role);
    const isViewMode = useStore(selectors.isViewMode);
    const isReviewer = relationshipModel.isReviewer(relationship, userId);
    const isAllowedToSign = getIsAllowedToSIgn({
        relationship,
        userId,
        role
    });
    const isNonSignatory = isReviewer || !isAllowedToSign;
    const hasSignature = !!signature;
    const showTermsText = hasSignature && !isPreview;
    const disabledReasonText = disabledReason ? reasonToText(disabledReason, reasonTexts) : null;
    const [hasAttemptedNext, setHasAttemptedNext] = useState(false);
    useEffect(()=>{
        setHasAttemptedNext(false);
    }, [
        currentStep
    ]);
    useEffect(()=>{
        if (isStatementAccepted) setHasAttemptedNext(false);
    }, [
        isStatementAccepted
    ]);
    const handleNextClick = ()=>{
        if (showCheckbox && !isStatementAccepted) {
            setHasAttemptedNext(true);
            return;
        }
        onNext();
    };
    const showCheckboxError = hasAttemptedNext && !!showCheckbox && !isStatementAccepted;
    const showAcceptTermsHint = !!showCheckbox && !isStatementAccepted;
    const showReviewServicesTooltip = currentStep === COBStep.reviewServices && !!hasNoServiceCommitted;
    const reviewServicesTooltipTitle = showReviewServicesTooltip ? footerTexts.selectAtLeastOneService() : null;
    const mobileTooltipWrappedNext = showAcceptTermsHint ? /*#__PURE__*/ _jsx(Tooltip, {
        title: footerTexts.acceptTermsRequired(),
        placement: "top",
        enableOnMobile: true,
        children: /*#__PURE__*/ _jsx("div", {
            className: styles.mobileFooterButton,
            "data-testid": FooterTestIds.AcceptTermsTooltip(),
            children: /*#__PURE__*/ _jsx(Button, {
                bi: {
                    name: 'onboarding_next_step'
                },
                onClick: handleNextClick,
                testId: FooterTestIds.NextButton(),
                disabled: isNextDisabled,
                fluid: true,
                children: nextStepButtonText
            })
        })
    }) : reviewServicesTooltipTitle != null ? /*#__PURE__*/ _jsx(Tooltip, {
        title: reviewServicesTooltipTitle,
        placement: "top",
        enableOnMobile: true,
        children: /*#__PURE__*/ _jsx("div", {
            className: styles.mobileFooterButton,
            "data-testid": FooterTestIds.ReviewServicesTooltip(),
            children: /*#__PURE__*/ _jsx(Button, {
                bi: {
                    name: 'onboarding_next_step_blocked'
                },
                onClick: handleNextClick,
                testId: FooterTestIds.NextButton(),
                disabled: isNextDisabled,
                fluid: true,
                children: nextStepButtonText
            })
        })
    }) : null;
    const desktopTooltipWrappedNext = showAcceptTermsHint ? /*#__PURE__*/ _jsx(Tooltip, {
        title: footerTexts.acceptTermsRequired(),
        placement: "top",
        children: /*#__PURE__*/ _jsx("div", {
            className: desktopStyles.button,
            "data-testid": FooterTestIds.AcceptTermsTooltip(),
            children: /*#__PURE__*/ _jsx(Button, {
                bi: {
                    name: 'onboarding_next_step'
                },
                onClick: handleNextClick,
                testId: FooterTestIds.NextButton(),
                disabled: isNextDisabled,
                fluid: true,
                children: nextStepButtonText
            })
        })
    }) : reviewServicesTooltipTitle != null ? /*#__PURE__*/ _jsx(Tooltip, {
        title: reviewServicesTooltipTitle,
        placement: "top",
        children: /*#__PURE__*/ _jsx("div", {
            className: desktopStyles.button,
            "data-testid": FooterTestIds.ReviewServicesTooltip(),
            children: /*#__PURE__*/ _jsx(Button, {
                bi: {
                    name: 'onboarding_next_step_blocked'
                },
                onClick: handleNextClick,
                testId: FooterTestIds.NextButton(),
                disabled: isNextDisabled,
                fluid: true,
                children: nextStepButtonText
            })
        })
    }) : null;
    const firstUnaccepted = unacceptedStatements === null || unacceptedStatements === void 0 ? void 0 : unacceptedStatements[0];
    const showApproveTermsHint = isSignAndApproveStep && firstUnaccepted != null && !!navigateToTermsStep;
    const renderApproveTermsHint = (variant)=>showApproveTermsHint && /*#__PURE__*/ _jsxs(Typography, {
            variant: variant,
            skin: "secondary",
            weight: "light",
            className: styles.signAndApproveHint,
            testId: FooterTestIds.ApproveTermsHint(),
            children: [
                footerTexts.pleaseApproveBefore(),
                ' ',
                /*#__PURE__*/ _jsx(TextButton, {
                    bi: {
                        name: 'open_terms_of_service'
                    },
                    onClick: ()=>navigateToTermsStep(firstUnaccepted.index),
                    variant: variant,
                    testId: FooterTestIds.ApproveTermsLink(),
                    children: firstUnaccepted.displayName
                }),
                ' ',
                footerTexts.toSignThisAgreement()
            ]
        });
    const approveButtonTooltipTitle = showApproveTermsHint && !isViewMode ? footerTexts.approveTermsFirst({
        termsName: firstUnaccepted.displayName
    }) : null;
    if (hideNextButton) {
        return /*#__PURE__*/ _jsx("div", {
            className: isMobile ? styles.mobileFooterSpacer : styles.footerSpacer
        });
    }
    if (isMobile) {
        const showBankSecurityRow = currentStep === COBStep.paymentMethod;
        return /*#__PURE__*/ _jsx(motion.div, {
            className: styles.mobileFooter,
            "data-testid": FooterTestIds.Root(),
            layout: true,
            transition: collapseTransition,
            children: /*#__PURE__*/ _jsxs(Column, {
                className: cn(styles.mobileFooterContent, isSignAndApproveStep && styles.mobileFooterContentCentered),
                gap: isSignAndApproveStep ? 0 : 16,
                children: [
                    isSignAndApproveStep && /*#__PURE__*/ _jsx(Typography, {
                        variant: "paragraph3",
                        skin: "secondary",
                        weight: "light",
                        className: cn(styles.signAndApproveHint, styles.signAndApproveHintCentered),
                        testId: FooterTestIds.SignAndApproveHint(),
                        children: showTermsText ? /*#__PURE__*/ _jsxs(_Fragment, {
                            children: [
                                footerTexts.byApprovingAgreement(),
                                /*#__PURE__*/ _jsx(TextButton, {
                                    bi: {
                                        name: 'open_terms_of_service_link'
                                    },
                                    as: "a",
                                    href: ANCHOR_TOS_URL,
                                    target: "_blank",
                                    rel: "noopener noreferrer",
                                    variant: "paragraph3",
                                    children: footerTexts.termsOfService()
                                }),
                                "."
                            ]
                        }) : isNonSignatory ? footerTexts.notRequiredToSign() : footerTexts.signAboveToApprove()
                    }),
                    /*#__PURE__*/ _jsx(AnimatePresence, {
                        initial: false,
                        children: showCheckbox && /*#__PURE__*/ _jsx(motion.div, {
                            variants: collapseVariants,
                            initial: "hidden",
                            animate: "visible",
                            exit: "hidden",
                            transition: collapseTransition,
                            style: {
                                overflow: 'hidden'
                            },
                            children: /*#__PURE__*/ _jsx(Checkbox, {
                                className: styles.statementCheckbox,
                                onChange: (e)=>onStatementChange(e.target.checked),
                                testId: FooterTestIds.StatementCheckbox(),
                                checked: isStatementAccepted,
                                error: showCheckboxError,
                                bi: {
                                    name: 'accept_statement'
                                },
                                children: /*#__PURE__*/ _jsx(Typography, {
                                    variant: "paragraph3",
                                    weight: "light",
                                    children: statement
                                })
                            })
                        }, "checkbox-collapse")
                    }),
                    showBankSecurityRow && /*#__PURE__*/ _jsx(BankSecurityRow, {
                        mobileView: true,
                        className: styles.bankSecurityRow,
                        "data-testid": PaymentMethodTestIds.BankSecurityRow()
                    }),
                    renderApproveTermsHint('paragraph3'),
                    isSignAndApproveStep && !showApproveTermsHint && disabledReasonText && /*#__PURE__*/ _jsx(Typography, {
                        variant: "paragraph3",
                        skin: "secondary",
                        weight: "light",
                        className: styles.signAndApproveHint,
                        testId: FooterTestIds.DisabledReason(),
                        children: disabledReasonText
                    }),
                    isSignAndApproveStep ? /*#__PURE__*/ _jsx("div", {
                        className: styles.mobileSignWrapper,
                        "data-testid": FooterTestIds.NextButton(),
                        children: /*#__PURE__*/ _jsx(MobileSign, {
                            relationship: relationship,
                            disabled: isNextDisabled,
                            intent: Intent.ApproveProposal,
                            onSign: onNext,
                            hideSignatoryTypography: true
                        })
                    }) : mobileTooltipWrappedNext !== null && mobileTooltipWrappedNext !== void 0 ? mobileTooltipWrappedNext : /*#__PURE__*/ _jsx(Button, {
                        bi: {
                            name: 'onboarding_next_step'
                        },
                        onClick: handleNextClick,
                        className: styles.mobileFooterButton,
                        testId: FooterTestIds.NextButton(),
                        disabled: isNextDisabled,
                        children: nextStepButtonText
                    })
                ]
            })
        });
    }
    const showSignAndApproveFooter = isSignAndApproveStep;
    return /*#__PURE__*/ _jsx(Row, {
        className: cn(desktopStyles.footer, {
            [styles.footerWithCheckbox]: showCheckbox,
            [styles.footerWithSignAndApprove]: showSignAndApproveFooter
        }),
        testId: FooterTestIds.Root(),
        children: /*#__PURE__*/ _jsxs(Row, {
            className: cn(styles.footerContent, {
                [styles.footerContentWithCheckbox]: showCheckbox,
                [styles.footerContentWithSignAndApprove]: showSignAndApproveFooter
            }),
            testId: FooterTestIds.FooterContent(),
            children: [
                showCheckbox && /*#__PURE__*/ _jsx(Row, {
                    className: styles.checkboxRow,
                    children: /*#__PURE__*/ _jsx(Checkbox, {
                        className: styles.statementCheckbox,
                        onChange: (e)=>onStatementChange(e.target.checked),
                        testId: FooterTestIds.StatementCheckbox(),
                        checked: isStatementAccepted,
                        error: showCheckboxError,
                        bi: {
                            name: 'accept_statement'
                        },
                        children: /*#__PURE__*/ _jsx(Typography, {
                            variant: "paragraph2",
                            weight: "light",
                            children: statement
                        })
                    })
                }),
                showSignAndApproveFooter && /*#__PURE__*/ _jsx(Typography, {
                    variant: "paragraph2",
                    skin: "secondary",
                    weight: "light",
                    className: styles.signAndApproveHint,
                    testId: FooterTestIds.SignAndApproveHint(),
                    children: showTermsText ? /*#__PURE__*/ _jsxs(_Fragment, {
                        children: [
                            footerTexts.byApprovingAgreement(),
                            /*#__PURE__*/ _jsx(TextButton, {
                                bi: {
                                    name: 'open_terms_of_service_link'
                                },
                                as: "a",
                                href: ANCHOR_TOS_URL,
                                target: "_blank",
                                rel: "noopener noreferrer",
                                variant: "paragraph2",
                                children: footerTexts.termsOfService()
                            }),
                            "."
                        ]
                    }) : isNonSignatory ? footerTexts.notRequiredToSign() : footerTexts.signAboveToApprove()
                }),
                renderApproveTermsHint('paragraph2'),
                showSignAndApproveFooter && !showApproveTermsHint && disabledReasonText && /*#__PURE__*/ _jsx(Typography, {
                    variant: "paragraph2",
                    skin: "secondary",
                    weight: "light",
                    className: styles.signAndApproveHint,
                    testId: FooterTestIds.DisabledReason(),
                    children: disabledReasonText
                }),
                approveButtonTooltipTitle != null ? /*#__PURE__*/ _jsx(Tooltip, {
                    title: approveButtonTooltipTitle,
                    placement: "top",
                    children: /*#__PURE__*/ _jsx("div", {
                        className: desktopStyles.button,
                        "data-testid": FooterTestIds.AcceptTermsTooltip(),
                        children: /*#__PURE__*/ _jsx(Button, {
                            onClick: handleNextClick,
                            testId: FooterTestIds.NextButton(),
                            disabled: isNextDisabled,
                            intent: isSignAndApproveStep ? Intent.ApproveProposal : undefined,
                            fluid: true,
                            bi: {
                                name: 'approve_agreement_blocked'
                            },
                            children: nextStepButtonText
                        })
                    })
                }) : desktopTooltipWrappedNext !== null && desktopTooltipWrappedNext !== void 0 ? desktopTooltipWrappedNext : /*#__PURE__*/ _jsx(Button, {
                    bi: {
                        name: 'onboarding_next_step'
                    },
                    onClick: handleNextClick,
                    className: desktopStyles.button,
                    testId: FooterTestIds.NextButton(),
                    disabled: isNextDisabled,
                    intent: isSignAndApproveStep ? Intent.ApproveProposal : undefined,
                    children: nextStepButtonText
                })
            ]
        })
    });
};
